import React, { useState, useRef, useCallback } from "react";
import { Autocomplete, TextField, Box } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { SEARCH_SYMBOLS } from "../../graphql/symbols";

export const SymbolSearch: React.FC<{ onSelect: (symbol: string) => void }> = ({
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [searchSymbols, { data }] = useLazyQuery(SEARCH_SYMBOLS);

  React.useEffect(() => {
    if (data?.searchSymbols) {
      setOptions(data.searchSymbols);
    }
  }, [data]);

  const debouncedSearch = useCallback(
    (searchQuery: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (searchQuery.length >= 1) {
          searchSymbols({ variables: { query: searchQuery } });
        } else {
          setOptions([]);
        }
      }, 300);
    },
    [searchSymbols],
  );

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleInputChange = (event: React.SyntheticEvent, value: string) => {
    setQuery(value);
    debouncedSearch(value);
  };

  return (
    <Box mt={2}>
      <Autocomplete
        freeSolo
        fullWidth
        options={options}
        inputValue={query}
        onInputChange={handleInputChange}
        onChange={(event, value) => {
          if (value) {
            onSelect(value);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Symbol"
            placeholder="Type to search..."
            autoFocus
          />
        )}
      />
    </Box>
  );
};
